import { useContext, useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  GeoJSON,
  Polygon,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faLink,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CasesContext from "contexts/CasesContext";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import egibService from "api/egibService";
import { IEgibParcel } from "dto/Egib/IEgibParcel";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

// WMS GUGiK — Krajowa Integracja Ewidencji Gruntów (warstwa działek).
const KIEG_URL =
  "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow";

// Zewnętrzny pierścień maski (cały świat) w formacie Leaflet [lat, lng].
const WORLD_RING: L.LatLngTuple[] = [
  [-85, -180],
  [85, -180],
  [85, 180],
  [-85, 180],
];

// ── Typy lekkiego słownika gmin (public/data/gminy.geojson) ───────────────────

type GminaGeometry =
  | { type: "Polygon"; coordinates: number[][][] }
  | { type: "MultiPolygon"; coordinates: number[][][][] };

interface GminaFeature {
  type: "Feature";
  properties: { teryt: string; name: string };
  geometry: GminaGeometry;
}

interface GminaOption {
  teryt: string;
  name: string;
}

// ── Pomocnicze: TERYT ─────────────────────────────────────────────────────────

// Kod gminy z identyfikatora działki ULDK, np. "141201_1.0001.6509" → "1412011".
// Przynależność do gminy wyróżniają cyfry 5–7 kodu TERYT (gmina + rodzaj),
// porównujemy jednak cały 7-cyfrowy kod gminy, który je zawiera.
const communeOfParcelId = (parcelId: string): string => {
  const head = (parcelId ?? "").split(".")[0];
  return head.replace(/\D/g, "").slice(0, 7);
};

// Zewnętrzne pierścienie gminy w formacie Leaflet [lat, lng] (otwory maski / kontur).
const exteriorRings = (geom: GminaGeometry): L.LatLngTuple[][] => {
  const polys =
    geom.type === "Polygon"
      ? [geom.coordinates]
      : (geom.coordinates as number[][][][]);
  return polys.map((poly) =>
    poly[0].map(([lng, lat]) => [lat, lng] as L.LatLngTuple),
  );
};

// ── Dopasowanie widoku do granic gminy ────────────────────────────────────────

const FitToGmina = ({ geom }: { geom: GminaGeometry | null }) => {
  const map = useMap();
  useEffect(() => {
    if (!geom) return;
    try {
      const bounds = L.geoJSON(geom as never).getBounds();
      if (bounds.isValid())
        map.fitBounds(bounds, { maxZoom: 14, padding: [20, 20] });
    } catch {
      /* ignore */
    }
  }, [geom, map]);
  return null;
};

// ── Klik na mapie → wyszukanie działki + walidacja przynależności do gminy ────

const ClickHandler = ({
  onPick,
}: {
  onPick: (lng: number, lat: number) => void;
}) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lng, e.latlng.lat),
  });
  return null;
};

const WasteMapModule = () => {
  const { cases } = useContext(CasesContext);

  const [features, setFeatures] = useState<GminaFeature[]>([]);
  const [byTeryt, setByTeryt] = useState<Map<string, GminaFeature>>(new Map());
  const [loadingDict, setLoadingDict] = useState(true);

  const [selected, setSelected] = useState<GminaOption | null>(null);
  const [showParcels, setShowParcels] = useState(true);

  const [idInput, setIdInput] = useState("");
  const [parcel, setParcel] = useState<IEgibParcel | null>(null);
  const [loadingParcel, setLoadingParcel] = useState(false);

  const [selectedCase, setSelectedCase] = useState<ICaseSimple | null>(null);
  const { data: caseParcels = [], refetch: refetchCaseParcels } =
    egibService.useGetCaseParcels(selectedCase?.Id);
  const linkMutation = egibService.useLinkParcel();
  const unlinkMutation = egibService.useUnlinkParcel();

  // Słownik gmin (TERYT + nazwa + geometria granic) — ładowany raz przy wejściu.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/data/gminy.geojson");
        const fc = (await res.json()) as { features: GminaFeature[] };
        if (cancelled) return;
        const feats = fc.features ?? [];
        setFeatures(feats);
        setByTeryt(new Map(feats.map((f) => [f.properties.teryt, f])));
      } catch {
        notification({
          type: ENotificationType.ERROR,
          title: "Gospodarka odpadami",
          text: "Nie udało się załadować słownika gmin.",
        });
      } finally {
        if (!cancelled) setLoadingDict(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo<GminaOption[]>(
    () =>
      features
        .map((f) => ({ teryt: f.properties.teryt, name: f.properties.name }))
        .sort((a, b) => a.name.localeCompare(b.name, "pl", { sensitivity: "base" })),
    [features],
  );

  const selectedFeature = selected ? byTeryt.get(selected.teryt) ?? null : null;
  const selectedGeom = selectedFeature?.geometry ?? null;

  // Maska: świat z „dziurami" w kształcie gminy — przyciemnia obszar poza gminą,
  // pozostawiając widoczne tylko działki położone w wybranej gminie.
  const maskPositions = useMemo<L.LatLngTuple[][] | null>(() => {
    if (!selectedGeom) return null;
    return [WORLD_RING, ...exteriorRings(selectedGeom)];
  }, [selectedGeom]);

  // Kontur gminy jako Feature (obrys granic zewnętrznych).
  const boundaryData = useMemo(
    () =>
      selectedGeom
        ? ({ type: "Feature", properties: {}, geometry: selectedGeom } as const)
        : null,
    [selectedGeom],
  );

  const handleError = (text: string) =>
    notification({ type: ENotificationType.ERROR, title: "Gospodarka odpadami", text });

  // Wyszukiwanie działki po identyfikatorze.
  const findById = async () => {
    const id = idInput.trim();
    if (!id) return;
    setLoadingParcel(true);
    try {
      const p = await egibService.getParcelById(id);
      setParcel(p);
    } catch {
      handleError("Nie znaleziono działki o podanym identyfikatorze.");
    } finally {
      setLoadingParcel(false);
    }
  };

  // Klik na mapie → wyszukanie działki po współrzędnych + walidacja gminy.
  const handlePick = async (lng: number, lat: number) => {
    if (!selected) {
      notification({
        type: ENotificationType.INFO,
        title: "Gospodarka odpadami",
        text: "Najpierw wyszukaj i wybierz gminę.",
      });
      return;
    }
    setLoadingParcel(true);
    try {
      const p = await egibService.getParcelByXY(lng, lat);
      if (communeOfParcelId(p.ParcelId) !== selected.teryt) {
        setParcel(null);
        notification({
          type: ENotificationType.INFO,
          title: "Gospodarka odpadami",
          text: "Wskazana działka leży poza wybraną gminą.",
        });
        return;
      }
      setParcel(p);
      if (p?.ParcelId) setIdInput(p.ParcelId);
    } catch {
      handleError("W tym miejscu nie znaleziono działki w EGiB.");
    } finally {
      setLoadingParcel(false);
    }
  };

  const handleLink = () => {
    if (!selectedCase) {
      handleError("Wybierz sprawę, do której chcesz powiązać działkę.");
      return;
    }
    if (!parcel?.ParcelId) return;
    linkMutation.mutate(
      {
        CaseId: selectedCase.Id,
        ParcelId: parcel.ParcelId,
        GeometryGeoJson: parcel.GeometryGeoJson,
        Wojewodztwo: parcel.Wojewodztwo,
        Powiat: parcel.Powiat,
        Gmina: parcel.Gmina,
        Obreb: parcel.Obreb,
      },
      {
        onSuccess: () => {
          refetchCaseParcels();
          notification({
            type: ENotificationType.SUCCESS,
            title: "Gospodarka odpadami",
            text: "Powiązano działkę ze sprawą.",
          });
        },
        onError: () => handleError("Nie udało się powiązać działki ze sprawą."),
      },
    );
  };

  const handleUnlink = (id: string) =>
    unlinkMutation.mutate(id, { onSuccess: () => refetchCaseParcels() });

  const showParcel = (gj?: string) => {
    if (!gj) return;
    setParcel((prev) => ({ ...prev, GeometryGeoJson: gj } as IEgibParcel));
  };

  const parcelGeom = useMemo<object | null>(() => {
    if (!parcel?.GeometryGeoJson) return null;
    try {
      return JSON.parse(parcel.GeometryGeoJson);
    } catch {
      return null;
    }
  }, [parcel]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.sidebar}>
        <div className={styles.title}>Gospodarka odpadami — mapa</div>

        {/* Wyszukiwanie gminy */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Wyszukaj gminę</div>
          <Autocomplete<GminaOption>
            size="small"
            options={options}
            loading={loadingDict}
            value={selected}
            onChange={(_, v) => {
              setSelected(v);
              setParcel(null);
            }}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.teryt === v.teryt}
            renderOption={(props, o) => (
              <li {...props} key={o.teryt}>
                <span style={{ flex: 1 }}>{o.name}</span>
                <span style={{ color: "#8a8693", fontSize: 11, fontFamily: "monospace" }}>
                  {o.teryt}
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Wyszukaj gminę"
                placeholder="Wpisz nazwę gminy…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingDict ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <div className={styles.hint}>
            Mapa pokazuje wyłącznie działki położone w wybranej gminie
            (wg kodu TERYT — cyfry 5–7).
          </div>
        </div>

        {/* Informacje o wybranej gminie */}
        {selected && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Wybrana gmina</div>
            <div className={styles.kv}>
              <span>Nazwa</span>
              <b>{selected.name}</b>
            </div>
            <div className={styles.kv}>
              <span>TERYT</span>
              <b style={{ fontFamily: "monospace" }}>{selected.teryt}</b>
            </div>
          </div>
        )}

        {/* Wyszukiwanie działki */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Wyszukaj działkę</div>
          <div className={styles.searchRow}>
            <TextField
              size="small"
              fullWidth
              label="Identyfikator działki"
              placeholder="np. 141201_1.0001.6509"
              value={idInput}
              onChange={(e) => setIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && findById()}
            />
            <Tooltip title="Szukaj">
              <span>
                <IconButton onClick={findById} disabled={loadingParcel || !idInput.trim()}>
                  {loadingParcel ? (
                    <CircularProgress size={18} />
                  ) : (
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <div className={styles.hint}>
            …albo kliknij na mapie, aby zlokalizować działkę.
          </div>
        </div>

        {/* Identyfikatory wybranej działki */}
        {parcel && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Działka</div>
            <div className={styles.kv}>
              <span>Identyfikator</span>
              <b>{parcel.ParcelId || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Województwo</span>
              <b>{parcel.Wojewodztwo || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Powiat</span>
              <b>{parcel.Powiat || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Gmina</span>
              <b>{parcel.Gmina || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Obręb</span>
              <b>{parcel.Obreb || "—"}</b>
            </div>
          </div>
        )}

        {/* Powiązanie ze sprawą */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Powiązanie ze sprawą</div>
          <Autocomplete
            size="small"
            options={cases}
            value={selectedCase}
            onChange={(_, v) => setSelectedCase(v)}
            getOptionLabel={(c) =>
              [c.Signature, c.Title].filter(Boolean).join(" — ") || c.Id
            }
            renderInput={(params) => (
              <TextField {...params} label="Sprawa / teczka" placeholder="Wybierz sprawę…" />
            )}
          />
          <Button
            className={styles.linkBtn}
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faLink} />}
            disabled={!parcel?.ParcelId || !selectedCase || linkMutation.isLoading}
            onClick={handleLink}
          >
            Powiąż działkę ze sprawą
          </Button>

          {selectedCase && (
            <div className={styles.linkedList}>
              <div className={styles.linkedTitle}>
                Działki sprawy ({caseParcels.length})
              </div>
              {caseParcels.map((cp) => (
                <div key={cp.Id} className={styles.linkedItem}>
                  <span
                    className={styles.linkedId}
                    title="Pokaż na mapie"
                    onClick={() => showParcel(cp.GeometryGeoJson)}
                  >
                    {cp.ParcelId}
                  </span>
                  <Tooltip title="Odepnij">
                    <IconButton size="small" onClick={() => handleUnlink(cp.Id)}>
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
              {caseParcels.length === 0 && (
                <div className={styles.hint}>Brak powiązanych działek.</div>
              )}
            </div>
          )}
        </div>

        {/* Warstwy */}
        <div className={styles.section}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showParcels}
                onChange={(e) => setShowParcels(e.target.checked)}
              />
            }
            label="Warstwa działek (KIEG)"
          />
          <div className={styles.hint}>
            {loadingParcel
              ? "Wyszukiwanie działki…"
              : selected
              ? "Kliknij na mapie, aby sprawdzić działkę."
              : "Wybierz gminę, aby zawęzić mapę."}
          </div>
        </div>
      </div>

      <div className={styles.mapWrap}>
        <MapContainer
          className={styles.map}
          center={[52.0, 19.4]}
          zoom={6}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {showParcels && (
            <WMSTileLayer
              url={KIEG_URL}
              layers="dzialki"
              format="image/png"
              transparent
              version="1.3.0"
              opacity={0.7}
            />
          )}

          {/* Przyciemnienie obszaru poza gminą */}
          {maskPositions && (
            <Polygon
              positions={maskPositions}
              pathOptions={{
                stroke: false,
                fillColor: "#ffffff",
                fillOpacity: 0.6,
              }}
              interactive={false}
            />
          )}

          {/* Kontur granic gminy */}
          {boundaryData && (
            <GeoJSON
              key={selected?.teryt}
              data={boundaryData as never}
              style={{ color: "#d32f2f", weight: 3, fill: false }}
            />
          )}

          {/* Podświetlenie wskazanej działki */}
          {parcelGeom && (
            <GeoJSON
              key={parcel?.ParcelId ?? "parcel"}
              data={parcelGeom as never}
              style={{ color: "#5d49c7", weight: 2, fillOpacity: 0.2 }}
            />
          )}

          {selectedGeom && <FitToGmina geom={selectedGeom} />}
          <ClickHandler onPick={handlePick} />
        </MapContainer>
      </div>
    </div>
  );
};

export default WasteMapModule;
